// Packages
import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { isMobileDevice } from "../utils/DetectMobile";
import { GeolocationUtils } from "../utils/GeolocationUtils";
import TaxId from "./TaxId";
// Components
import CustomFieldList from "./CustomFieldList";
import PhoneInput from "./custom_fields/PhoneInput/PhoneInput";
import {
  getCountryPhonePrefix,
  isPhoneNumberValid,
  getPhonePrefixCountry,
} from "./custom_fields/PhoneInput/helpers";
import { faCircleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import selectReactStyle from "./SelectReactStyle";

const PostalCode = ({
  address,
  errors,
  doingRequest,
  changeStoreField,
  handlePostalBlur,
  t,
  systemFields,
  no_shipping_required,
  countries,
}) => {
  return (
    <div
      className={`col-sm-6 ${countries.length === 1 && address.country ? " pe-sm-1" : " ps-sm-1"} mb-3`}
    >
      <div
        className={`floating-label-wrap ${
          errors.postal ? "error-div-highlight" : ""
        }`}
      >
        <label htmlFor="postal" className="label-checkout">
          {systemFields.postal_required
            ? ["PT", "BR", "US", "GB", "MX", "AR", "CL", "PE", "CO"].includes(
                address.country,
              )
              ? t(`information.${address.country.toLowerCase()}-postal`) + " *"
              : t("information.postal") + " *"
            : ["PT", "BR", "US", "GB", "MX", "AR", "CL", "PE", "CO"].includes(
                  address.country,
                )
              ? t(`information.${address.country.toLowerCase()}-postal`) +
                t("information.optional")
              : t("information.postal-optional")}
        </label>
        <input
          type="text"
          className="floating-label-field floating-label-field--s1"
          id="postal"
          disabled={doingRequest}
          placeholder={
            systemFields.postal_required
              ? ["PT", "BR", "US", "GB", "MX", "AR", "CL", "PE", "CO"].includes(
                  address.country,
                )
                ? t(`information.${address.country.toLowerCase()}-postal`) +
                  " *"
                : t("information.postal") + " *"
              : ["PT", "BR", "US", "GB", "MX", "AR", "CL", "PE", "CO"].includes(
                    address.country,
                  )
                ? t(`information.${address.country.toLowerCase()}-postal`) +
                  t("information.optional")
                : t("information.postal-optional")
          }
          onChange={(e) => {
            changeStoreField("postal", e.target.value);
          }}
          onBlur={(e) => {
            handlePostalBlur(address.country, e.target.value);
          }}
          value={(address != null && address.postal) || ""}
          autoComplete="postal-code"
          required={address.country === "AR" ? true : false}
        />

        {address.country == "BR" && (
          <a
            href="https://buscacepinter.correios.com.br/app/endereco/index.php"
            target="_blank"
            rel="noopener noreferrer"
          >
            <small
              className="position-absolute pe-2"
              style={{
                right: "0",
                top: "50%",
                transform: "translate(0, -50%)",
              }}
            >
              {t("information.br-find-cep")}
            </small>
          </a>
        )}
      </div>

      <div
        className={`field-error ${errors.postal ? "" : "hidden-error"}`}
        style={{
          display: errors.postal ? "flex" : "",
          alignItems: "center",
        }}
      >
        <FontAwesomeIcon
          icon={faCircleExclamation}
          style={{ paddingRight: "2px" }}
          size="sm"
          fixedWidth
        />
        {t(errors.postal)}
      </div>
    </div>
  );
};

const AddressForm = (props) => {
  const {
    address,
    countries,
    regions,
    municipalities,
    type,
    customFields,
    no_shipping_required = false,
    request_geolocation = "1",
    phone,
    order_country,
    errors,
    setFieldBilling,
    setFieldInformation,
    callUpdateLocations,
    callUpdateCountry,
    callUpdateRegion,
    addressAutofill,
    systemFields,
    setCustomField,
    AdditionalInformation,
    validateTaxid,
    informationName,
    informationSurname,
    invoiceFields,
    setInvoiceFields,
    information = null,
    billing = null,
    handleShowBilling = () => false,
  } = props;

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { colors, urls } = useSelector((state) => state.checkoutStore);
  // TODO: Remove/fix? Does customers store even exist?
  const storeCountry = useSelector(
    (state) => state.customersStore?.storeCountry,
  );
  const doingRequest = useSelector((state) => state.ui.doingRequest);
  const [currentPhone, setCurrentPhone] = useState(
    (phone != null && phone) || "",
  );
  const [currentPrefix, setCurrentPrefix] = useState(
    address.country != ""
      ? getCountryPhonePrefix("", address.country)
      : order_country != ""
        ? getCountryPhonePrefix("", order_country)
        : "",
  );
  const [phoneError, setPhoneError] = useState({ value: false, message: "" });

  useEffect(() => {
    if (!address.address || !address.country) {
      if (isMobileDevice() && request_geolocation == "1") {
        navigator.geolocation.getCurrentPosition(getGeolocation, getIpLocation);
      } else {
        getIpLocation();
      }
      initialName();
      initialSurname();
    }
  }, []);

  const handleBlurTaxID = () => {
    if (
      address.taxid != null &&
      address.taxid != "" &&
      address.country != null &&
      address.country != ""
    ) {
      validateTaxid();
    }
  };

  const getGeolocation = (position) => {
    if (doingRequest) {
      return;
    }

    const storage = getStorage();
    let country, region, municipality;

    if (!!storage && !!storage.country_code) {
      country = storage.country_code;
      region = storage.region;
      municipality = storage.municipality;
    }

    const coordinates = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
    };

    GeolocationUtils(
      dispatch,
      urls.geo_code_data,
      coordinates,
      country,
      region,
      municipality,
      type === "shipping",
    );
  };

  const getIpLocation = () => {
    const storage = getStorage();
    if (storage == null) {
      return;
    }

    if (
      (address.country == null || address.country == "") &&
      storage.country_code != null &&
      storage.country_code != ""
    ) {
      callUpdateLocations(
        storage.country_code,
        storage.region,
        storage.municipality,
        storage.city,
      );
    }
  };

  const getStorage = () => {
    if (
      localStorage.getItem("geolocation") == null ||
      localStorage.getItem("geolocation") == ""
    ) {
      return null;
    }
    return JSON.parse(localStorage.getItem("geolocation"));
  };

  const changeStoreField = (field, value) => {
    if (type === "billing" && field !== "phone" && field !== "phone_prefix") {
      setFieldBilling(field, value);
    } else {
      setFieldInformation(field, value);
    }
  };

  const initialName = () => {
    if (address != null && address.name === "") {
      changeStoreField("name", informationName);
    }
  };

  const initialSurname = () => {
    if (address != null && address.surname === "") {
      changeStoreField("surname", informationSurname);
    }
  };

  const handlePostalBlur = (country, postal) => {
    const addressInput = document.getElementById("address");
    if (addressInput && addressInput.value.trim() === "") {
      addressAutofill(country, postal);
    }
  };
  const shouldDisplayPostalCode =
    type === "shipping"
      ? systemFields.shipping_postal_enabled ||
        address.country == "BR" ||
        address.country == "AR"
      : systemFields.billing_postal_enabled ||
        (address.country == "BR" && !no_shipping_required);

  useEffect(() => {
    validatePhone(currentPhone);
  }, [currentPrefix, currentPhone]);

  const validatePhone = (phoneNumber) => {
    let phone = phoneNumber == null ? "" : phoneNumber;
    let prefix =
      currentPrefix == null
        ? getCountryPhonePrefix("", storeCountry)
        : currentPrefix;
    let isValidPhone = isPhoneNumberValid(phone, prefix);

    if (!isValidPhone) {
      setPhoneError({ value: true, message: t("information.phone-not-valid") });
    } else {
      setPhoneError({ value: false, message: "" });
      changeStoreField("phone", currentPhone);
      changeStoreField("phone_prefix", currentPrefix);
    }
  };

  const defaultColor = "#929292";

  const customStyles = (isError) => ({
    control: (base) => ({
      ...base,
      borderRadius: "6px",
      height: "44px",
      padding: "0 8px",
      fontSize: "14px",
      border: isError ? "2px solid #ed2c2c !important;" : "",
      backgroundColor: isError ? "#feeded !important" : "white",
    }),
    placeholder: (base) => ({
      ...base,
      color: defaultColor,
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: defaultColor,
      padding: "8px",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    valueContainer: (base) => ({
      ...base,
      padding: "4px 8px",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? defaultColor
        : state.isFocused
          ? "#f0f0f0"
          : "#fff",
      color: state.isSelected ? colors.text_color : "#000",
      padding: "8px 12px",
      cursor: "pointer",
    }),
  });

  return (
    <div className="row">
      <div className="col-sm-6 pe-sm-1 mb-3">
        <div
          className={`floating-label-wrap ${
            errors.name ? "error-div-highlight" : ""
          }`}
        >
          <label htmlFor="name" className="label-checkout">
            {t("information.name")} *
          </label>
          <input
            type="text"
            required
            className="floating-label-field floating-label-field--s1"
            id="name"
            disabled={doingRequest}
            placeholder={t("information.name")}
            onChange={(e) => changeStoreField("name", e.target.value)}
            value={address.name}
            autoComplete="given-name"
          />
        </div>

        <div
          className={`field-error ${errors.name ? "" : "hidden-error"}`}
          style={{
            display: errors.name ? "flex" : "",
            alignItems: "center",
          }}
        >
          <FontAwesomeIcon
            icon={faCircleExclamation}
            style={{ paddingRight: "2px" }}
            size="sm"
            fixedWidth
          />
          {t(errors.name)}
        </div>
      </div>

      <div className="col-sm-6 ps-sm-1 mb-3">
        <div
          className={`floating-label-wrap ${
            errors.surname ? "error-div-highlight" : ""
          }`}
        >
          <label htmlFor="surname" className="label-checkout">
            {t("information.surname")} *
          </label>
          <input
            type="text"
            required
            className="floating-label-field floating-label-field--s1"
            id="surname"
            disabled={doingRequest}
            placeholder={t("information.surname")}
            onChange={(e) => changeStoreField("surname", e.target.value)}
            value={address.surname}
            autoComplete="family-name"
          />
        </div>
        <div
          className={`field-error ${errors.surname ? "" : "hidden-error"}`}
          style={{
            display: errors.surname ? "flex" : "",
            alignItems: "center",
          }}
        >
          <FontAwesomeIcon
            icon={faCircleExclamation}
            style={{ paddingRight: "2px" }}
            size="sm"
            fixedWidth
          />
          {t(errors.surname)}
        </div>
      </div>

      {countries.length === 1 && address.country ? null : (
        <div className="col-sm-6 pe-sm-1 mb-3">
          <div
            className={`floating-label-wrap ${
              errors.country ? "error-div-highlight" : ""
            }`}
          >
            <label htmlFor="country" className="label-checkout">
              {t("information.country")} *
            </label>
            <div>
              <Select
                styles={customStyles(errors.country)}
                searchable
                classNamePrefix="custom-select"
                placeholder={t("information.select")}
                id="country"
                options={countries}
                onChange={(row) => callUpdateCountry(row.value)}
                value={
                  (address != null &&
                    countries.find(
                      (current) => current.value === address.country,
                    )) ||
                  ""
                }
              />
            </div>
          </div>
          <div
            className={`field-error ${errors.country ? "" : "hidden-error"}`}
            style={{
              display: errors.country ? "flex" : "",
              alignItems: "center",
            }}
          >
            <FontAwesomeIcon
              icon={faCircleExclamation}
              style={{ paddingRight: "2px" }}
              size="sm"
              fixedWidth
            />
            {t(errors.country)}
          </div>
        </div>
      )}

      {shouldDisplayPostalCode && (
        <PostalCode
          address={address}
          errors={errors}
          doingRequest={doingRequest}
          changeStoreField={changeStoreField}
          handlePostalBlur={handlePostalBlur}
          t={t}
          systemFields={systemFields}
          no_shipping_required={no_shipping_required}
          countries={countries}
        />
      )}

      {regions.length == 0 ? null : (
        <div className="col-12 mb-3">
          <div
            className={`floating-label-wrap ${
              errors.region ? "error-div-highlight" : ""
            }`}
          >
            <label htmlFor="region" className="label-checkout">
              {address.country == "CR"
                ? t("information.province")
                : t("information.region")}{" "}
              *
            </label>
            <div>
              <Select
                styles={customStyles(errors.region)}
                classNamePrefix="custom-select"
                placeholder={t("information.select")}
                searchable
                id="region"
                options={regions}
                onChange={(row) => callUpdateRegion(row.value)}
                value={
                  (address != null &&
                    regions.find(
                      (current) => current.value === address.region,
                    )) ||
                  ""
                }
                autoComplete="address-level1"
              />
            </div>
          </div>
          <div
            className={`field-error ${errors.region ? "" : "hidden-error"}`}
            style={{
              display: errors.region ? "flex" : "",
              alignItems: "center",
            }}
          >
            <FontAwesomeIcon
              icon={faCircleExclamation}
              style={{ paddingRight: "2px" }}
              size="sm"
              fixedWidth
            />
            {t(errors.region)}
          </div>
        </div>
      )}

      {municipalities.length > 0 && (
        <div className="col-12 mb-3">
          <div
            className={`floating-label-wrap ${
              errors.municipality ? "error-div-highlight" : ""
            }`}
          >
            <label htmlFor="municipality" className="label-checkout">
              {t("information.municipality")} *
            </label>
            <div>
              <Select
                styles={customStyles(errors.municipality)}
                classNamePrefix="custom-select"
                placeholder={t("information.select")}
                searchable
                id="municipality"
                options={municipalities}
                onChange={(row) => changeStoreField("municipality", row.value)}
                value={
                  (address != null &&
                    municipalities.find(
                      (current) => current.value === address.municipality,
                    )) ||
                  ""
                }
                autoComplete="address-level3"
              />
            </div>
          </div>
          <div
            className={`field-error ${
              errors.municipality ? "" : "hidden-error"
            }`}
            style={{
              display: errors.municipality ? "flex" : "",
              alignItems: "center",
            }}
          >
            <FontAwesomeIcon
              icon={faCircleExclamation}
              style={{ paddingRight: "2px" }}
              size="sm"
              fixedWidth
            />
            {t(errors.municipality)}
          </div>
        </div>
      )}

      {address.country != "CO" && address.country != "PE" && (
        <div className="col-12 mb-3 form-group">
          <div
            className={`floating-label-wrap ${
              errors.city ? "error-div-highlight" : ""
            }`}
          >
            <label htmlFor="city" className="label-checkout">
              {address.country == "CR"
                ? t("information.district")
                : t("information.city")}{" "}
              *
            </label>
            <input
              type="text"
              className="floating-label-field floating-label-field--s1"
              id="city"
              disabled={doingRequest}
              placeholder={t("information.city")}
              onChange={(e) => changeStoreField("city", e.target.value)}
              value={(address != null && address.city) || ""}
              autoComplete="address-level2"
            />
          </div>
          <div
            className={`field-error ${errors.city ? "" : "hidden-error"}`}
            style={{
              display: errors.city ? "flex" : "",
              alignItems: "center",
            }}
          >
            <FontAwesomeIcon
              icon={faCircleExclamation}
              style={{ paddingRight: "2px" }}
              size="sm"
              fixedWidth
            />
            {t(errors.city)}
          </div>
        </div>
      )}

      <div className="col-12 mb-3 form-group">
        <div
          className={`floating-label-wrap ${
            errors.address ? "error-div-highlight" : ""
          }`}
        >
          <label htmlFor="address" className="label-checkout">
            {t("information.address")} *
          </label>
          <input
            type="text"
            required
            className="floating-label-field floating-label-field--s1"
            id="address"
            disabled={doingRequest}
            placeholder={t("information.address")}
            onChange={(e) => changeStoreField("address", e.target.value)}
            value={(address != null && address.address) || ""}
            autoComplete="street-address"
          />
        </div>
        <div
          className={`field-error ${errors.address ? "" : "hidden-error"}`}
          style={{
            display: errors.address ? "flex" : "",
            alignItems: "center",
          }}
        >
          <FontAwesomeIcon
            icon={faCircleExclamation}
            style={{ paddingRight: "2px" }}
            size="sm"
            fixedWidth
          />
          {t(errors.address)}
        </div>
      </div>

      <div className="col-12 mb-3 form-group">
        <div
          className={`floating-label-wrap ${
            errors.complement ? "error-div-highlight" : ""
          }`}
        >
          <label htmlFor="complement" className="label-checkout">
            {t("information.complement")}
          </label>
          <input
            type="text"
            className="floating-label-field floating-label-field--s1"
            id="complement"
            disabled={doingRequest}
            placeholder={t("information.complement")}
            onChange={(e) => changeStoreField("complement", e.target.value)}
            value={(address != null && address.complement) || ""}
          />
        </div>
        <div
          className={`field-error ${errors.complement ? "" : "hidden-error"}`}
          style={{
            display: errors.complement ? "flex" : "",
            alignItems: "center",
          }}
        >
          <FontAwesomeIcon
            icon={faCircleExclamation}
            style={{ paddingRight: "2px" }}
            size="sm"
            fixedWidth
          />
          {t(errors.complement)}
        </div>
      </div>

      {type === "billing" &&
        no_shipping_required &&
        systemFields.phone_enabled && (
          <Fragment>
            <PhoneInput
              flagId={"prefix"}
              flagValue={getPhonePrefixCountry(currentPrefix, address.country)}
              flagPlaceholder={t("information.phone-code")}
              onFlagChange={(e) => {
                setCurrentPrefix(e.value);
              }}
              phoneValue={currentPhone || ""}
              phoneId={"phone"}
              phonePlaceholder={
                systemFields.phone_required
                  ? t("information.phone")
                  : t("information.phone-optional")
              }
              onNumberChange={(number) => {
                setCurrentPhone(number.replace(/ /g, ""));
              }}
              phoneError={t(phoneError.message)}
              phoneEmpty={t(errors.phone)}
            />
          </Fragment>
        )}

      {customFields.length > 0 ? (
        <CustomFieldList
          customFields={customFields}
          errors={errors}
          setCustomField={setCustomField}
          AdditionalInformation={
            systemFields.additional_information ? AdditionalInformation : null
          }
          TaxId={
            <TaxId
              billingCountry={billing?.country}
              type={type}
              systemFields={systemFields}
              information={information}
              handleShowBilling={handleShowBilling}
              handleBlurTaxID={handleBlurTaxID}
              errors={errors}
              changeStoreField={changeStoreField}
              invoiceFields={invoiceFields}
              setInvoiceFields={setInvoiceFields}
              isAdressForm={true}
            />
          }
        />
      ) : null}
    </div>
  );
};
export default AddressForm;
